import React, { useEffect } from 'react';
import { Link } from 'react-router-dom';
import { ArrowRight, BookOpen, Calendar, Clock, Tag, Sparkles } from 'lucide-react';
import articles from '../../content/articles-manifest.json';
import { COMPANY_BRAND_NAME, COMPANY_TAGLINE } from '../../support/brand';
import { COMPANY_NAME, COMPANY_CNPJ } from '../../support/legal';
import { SAME_AS_LINKS } from '../../support/social';
import { PANEL_LEAD_URL } from '../../support/publicUrls';

export const ARTICLES_INDEX_SEO = {
  title: 'Artigos e Guias sobre Agentes de IA e WhatsApp | MAIA',
  description:
    'Aprenda como criar, configurar e operar agentes de inteligência artificial autônomos para WhatsApp, atendimento e produtividade com a MAIA.',
  canonical: 'https://okmaia.com/artigos',
};

export const ARTICLES_INDEX_STRUCTURED_DATA = [
  {
    '@context': 'https://schema.org',
    '@type': 'CollectionPage',
    name: ARTICLES_INDEX_SEO.title,
    description: ARTICLES_INDEX_SEO.description,
    url: ARTICLES_INDEX_SEO.canonical,
    publisher: {
      '@type': 'Organization',
      name: COMPANY_BRAND_NAME,
      legalName: COMPANY_NAME,
      taxID: COMPANY_CNPJ,
      url: 'https://okmaia.com/',
      logo: 'https://okmaia.com/logo.png',
      sameAs: SAME_AS_LINKS,
    },
  },
];

export function renderArticlesIndexHeadTags() {
  const tags = [
    `<title>${ARTICLES_INDEX_SEO.title}</title>`,
    `<meta name="description" content="${ARTICLES_INDEX_SEO.description}" />`,
    `<link rel="canonical" href="${ARTICLES_INDEX_SEO.canonical}" />`,
    `<meta property="og:title" content="${ARTICLES_INDEX_SEO.title}" />`,
    `<meta property="og:description" content="${ARTICLES_INDEX_SEO.description}" />`,
    `<meta property="og:url" content="${ARTICLES_INDEX_SEO.canonical}" />`,
    `<meta property="og:type" content="website" />`,
    `<script data-articles-static="true" type="application/ld+json">${JSON.stringify(
      ARTICLES_INDEX_STRUCTURED_DATA
    ).replaceAll('<', '\\u003c')}</script>`,
  ];
  return tags.join('\n    ');
}

export function renderArticlesIndexMarkdown() {
  const lines = [
    `# ${ARTICLES_INDEX_SEO.title}`,
    '',
    ARTICLES_INDEX_SEO.description,
    '',
    `Publicado pela ${COMPANY_BRAND_NAME} (${COMPANY_NAME} · CNPJ ${COMPANY_CNPJ}) — ${COMPANY_TAGLINE}.`,
    '',
    '## Artigos publicados',
    '',
  ];

  articles.forEach((art) => {
    lines.push(
      `### [${art.title}](https://okmaia.com/artigos/${art.slug})`,
      `*${art.date} · ${art.category} · ${art.readTime}*`,
      '',
      art.description,
      ''
    );
  });

  lines.push(
    '---',
    '',
    '## Sobre a MAIA',
    'A MAIA desenvolve tecnologia para criação e operação de agentes de inteligência artificial autônomos. Conheça as soluções:',
    '- [Plataforma MAIA](https://okmaia.com/): criação e orquestração de agentes de IA.',
    '- [Agente de IA para WhatsApp](https://okmaia.com/agente-ia-whatsapp): integração oficial à WhatsApp Business Cloud API.',
    '- [Mentoria Digital](https://okmaia.com/mentor-ia): transforme conhecimento e cursos em mentoria individual no WhatsApp.',
    ''
  );

  return lines.join('\n');
}

function ArticlesIndex() {
  useEffect(() => {
    document.title = ARTICLES_INDEX_SEO.title;
  }, []);

  return (
    <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
      <header className="max-w-3xl mb-12">
        <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-forge-50 border border-forge-200 text-xs font-semibold text-forge-700 uppercase tracking-wider mb-4">
          <BookOpen className="w-3.5 h-3.5" />
          Conhecimento e Tecnologia
        </div>
        <h1 className="text-4xl font-bold tracking-tight text-content-primary sm:text-5xl">
          Artigos, guias e boas práticas de IA
        </h1>
        <p className="mt-4 text-lg text-content-secondary leading-relaxed">
          Conteúdo prático sobre arquitetura de agentes autônomos, conexão oficial no WhatsApp,
          integrações corporativas e o futuro do atendimento inteligente produzido pela equipe de engenharia e produto da MAIA.
        </p>
      </header>

      <div className="grid gap-8 md:grid-cols-2">
        {articles.map((article) => (
          <article
            key={article.slug}
            className="surface-card flex flex-col justify-between p-8 transition-all duration-200 hover:border-forge-300 hover:shadow-card-md group"
          >
            <div>
              <div className="flex items-center gap-4 text-xs text-content-muted mb-4">
                <span className="inline-flex items-center gap-1 font-medium text-forge-600 bg-forge-50 px-2.5 py-1 rounded-lg">
                  <Tag className="w-3 h-3" />
                  {article.category}
                </span>
                <span className="flex items-center gap-1">
                  <Calendar className="w-3.5 h-3.5" />
                  {article.date}
                </span>
                <span className="flex items-center gap-1">
                  <Clock className="w-3.5 h-3.5" />
                  {article.readTime}
                </span>
              </div>

              <h2 className="text-2xl font-bold text-content-primary group-hover:text-forge-600 transition-colors">
                <Link to={`/artigos/${article.slug}`}>{article.title}</Link>
              </h2>

              <p className="mt-3 text-content-secondary leading-relaxed text-sm">
                {article.description}
              </p>
            </div>

            <div className="mt-6 pt-4 border-t border-slate-100 flex items-center justify-between">
              <span className="text-xs text-content-subtle">Por {article.author}</span>
              <Link
                to={`/artigos/${article.slug}`}
                className="inline-flex items-center gap-1.5 text-sm font-semibold text-forge-600 hover:text-forge-700 group-hover:translate-x-0.5 transition-transform"
              >
                Ler artigo completo
                <ArrowRight className="w-4 h-4" />
              </Link>
            </div>
          </article>
        ))}
      </div>

      <section className="mt-16 rounded-3xl bg-slate-900 text-white p-8 sm:p-12 text-center">
        <div className="inline-flex items-center gap-1.5 px-3 py-1 rounded-full bg-slate-800 border border-slate-700 text-xs font-semibold text-forge-400 uppercase tracking-wider mb-4">
          <Sparkles className="w-3.5 h-3.5" />
          Tecnologia MAIA
        </div>
        <h2 className="text-2xl sm:text-3xl font-bold">
          Quer colocar um agente de IA no ar na sua empresa?
        </h2>
        <p className="mt-3 text-slate-300 max-w-2xl mx-auto text-sm sm:text-base">
          A MAIA desenvolve tecnologia para você criar, configurar e operar agentes autônomos integrados a bases de conhecimento e canais como o WhatsApp com total conformidade e estabilidade.
        </p>
        <div className="mt-6 flex flex-col sm:flex-row items-center justify-center gap-4">
          <a href={PANEL_LEAD_URL} className="btn-primary !bg-white !text-slate-900 hover:!bg-slate-100">
            Criar conta gratuita
          </a>
          <a
            href="https://wa.me/5521971792112?text=Ol%C3%A1%2C%20gostaria%20de%20conversar%20sobre%20a%20MAIA."
            target="_blank"
            rel="noreferrer"
            className="text-sm text-slate-300 hover:text-white underline underline-offset-4"
          >
            Falar com especialista no WhatsApp
          </a>
        </div>
        <div className="mt-8 pt-6 border-t border-slate-800/80 flex flex-wrap justify-center items-center gap-x-6 gap-y-2 text-xs text-slate-400">
          <span>Soluções MAIA:</span>
          <Link to="/" className="hover:text-white underline underline-offset-2">Plataforma de Agentes</Link>
          <Link to="/agente-ia-whatsapp" className="hover:text-white underline underline-offset-2">IA para WhatsApp</Link>
          <Link to="/mentor-ia" className="hover:text-white underline underline-offset-2">Mentoria Digital</Link>
        </div>
      </section>
    </div>
  );
}

export default ArticlesIndex;
